import {
  StoreResponse,
  StoreScheduleExclusionResponse,
  StoreScheduleStoreExclusionResponse,
} from '@ydistri/api-sdk';

export type StoreComparator = (left: StoreResponse, right: StoreResponse) => number;

/**
 * By store, so the records of one store sit together and an overlap is visible as adjacent rows.
 * The backend sends no ordering, so this is what makes the list readable.
 */
export const compareForDisplay =
  (compareStores: StoreComparator) =>
  (left: StoreScheduleExclusionResponse, right: StoreScheduleExclusionResponse): number =>
    compareStores(left.store, right.store) ||
    (left.excludedUntil ?? '').localeCompare(right.excludedUntil ?? '') ||
    left.storeScheduleId - right.storeScheduleId;

export interface ExclusionBuckets {
  notSending: StoreScheduleStoreExclusionResponse[];
  notReceiving: StoreScheduleStoreExclusionResponse[];
  completelyExcluded: StoreScheduleStoreExclusionResponse[];
}

/**
 * The three buckets are disjoint: a completely excluded store appears only in the third one, never
 * also in the two partial lists.
 */
export const buckets = (
  storeExclusions: StoreScheduleStoreExclusionResponse[],
): ExclusionBuckets => ({
  notSending: storeExclusions.filter(e => e.isExcludedFromSending && !e.isCompletelyExcluded),
  notReceiving: storeExclusions.filter(e => e.isExcludedFromReceiving && !e.isCompletelyExcluded),
  completelyExcluded: storeExclusions.filter(e => e.isCompletelyExcluded),
});

export const describeExclusion = (exclusion: StoreScheduleExclusionResponse): string => {
  if (exclusion.isExcludedFromSending && exclusion.isExcludedFromReceiving)
    return 'Cannot send or receive';
  if (exclusion.isExcludedFromSending) return 'Cannot send';
  if (exclusion.isExcludedFromReceiving) return 'Cannot receive';
  return '';
};
